import { ALL_COLORS, BRAND_COLORS, COLOR_SCHEMES, MONO_COLORS, type BrandColor, type ColorScheme } from "./brand"
import { useMemo } from "react"
import type { ProcessorArgs, ShapeStyle } from "./lib/processor"
import type { GeneratorSettings } from "./settings"

export function useSpec(settings: GeneratorSettings) {
  return useMemo(() => {
    let styles: ShapeStyle[] = []
    let schemeColors: ColorScheme = {
      bg: "white",
      fg: "black",
      bgColor: MONO_COLORS.white,
      fgColor: MONO_COLORS.black,
    }

    if (settings.scheme) {
      if (settings.scheme === "multicolor") {
        schemeColors = settings.invertScheme
          ? {
              bg: "black",
              fg: "white",
              bgColor: MONO_COLORS.black,
              fgColor: MONO_COLORS.white,
            }
          : {
              bg: "white",
              fg: "black",
              bgColor: MONO_COLORS.white,
              fgColor: MONO_COLORS.black,
            }
        for (const [name, color] of Object.entries(ALL_COLORS)) {
          if (!settings.allowedColors.includes(name as BrandColor)) continue
          if (settings.outline || settings.renderMode === "draw") {
            styles.push({
              fill: "none",
              stroke: color,
              strokeWidth: settings.lineWidth,
            })
          } else {
            styles.push({
              fill: color,
              stroke: "none",
              strokeWidth: settings.lineWidth,
            })
          }
        }
      } else {
        const scheme = COLOR_SCHEMES[settings.scheme]
        if (scheme) {
          const colors = settings.invertScheme ? scheme.inverted : scheme.default
          schemeColors = colors
          if (settings.outline || settings.renderMode === "draw") {
            styles.push({
              fill: "none",
              stroke: colors.fgColor,
              strokeWidth: settings.lineWidth,
            })
          } else {
            styles.push({
              fill: colors.fgColor,
              stroke: "none",
              strokeWidth: settings.lineWidth,
            })
          }
        }
      }
    }

    if (settings.debugShapes) {
      styles = []
      schemeColors.bg = "white"
      schemeColors.fg = "black"
      schemeColors.bgColor = MONO_COLORS.white
      schemeColors.fgColor = MONO_COLORS.black
      // Add a few distinct colors
      for (let i = 0; i < 5; i++) {
        styles.push({
          strokeWidth: 3,
          stroke: `hsl(${(i * 5123.523414) % 360}, 100%, 50%)`,
          fill: "none",
        })
      }
    }

    if (styles.length === 0) {
      if (settings.outline || settings.renderMode === "draw") {
        styles.push({
          fill: "none",
          stroke: MONO_COLORS.black,
          strokeWidth: settings.lineWidth,
        })
      } else {
        styles.push({
          fill: MONO_COLORS.black,
          stroke: "none",
          strokeWidth: settings.lineWidth,
        })
      }
    }

    const spec: ProcessorArgs = {
      size: { rows: settings.rows, cols: settings.cols },
      seed: settings.seed,
      warp: settings.warp,
      shapeStyles: styles,
      scheme: schemeColors,
      transparent: true,
      density: settings.density,
      voids: settings.voids,
      downsample: settings.downsample,
      sharpness: settings.sharpness,
      lineOffset: settings.lineOffset,
      targetShape: settings.targetShape,
      animatedStyling: settings.autoWarpOn || settings.mouseOn,
      stickyStyles: settings.renderMode !== "static",
    }
    return spec
  }, [settings])
}
